if (!customElements.get("collection-tabs")) {
  class CollectionTabs extends HTMLElement {
    constructor() {
      super();

      this._onResize = null;
      this._onBlockSelect = null;

      this._fetchCache = new Map();
      this._fetchPromises = new Map();
    }

    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll("button"));

      const sectionHeader = this.closest(".section-header");

      this.links = sectionHeader
        ? Array.from(sectionHeader.querySelectorAll(".linked-to-tab"))
        : [];

      this.target = this.dataset.target;
      this.slider = document.getElementById(this.target);

      if (!this.slider) return;

      this.productLimit =
        parseInt(this.slider.dataset.productLimit, 10) || 6;

      this.columnsDesktop =
        parseInt(this.slider.dataset.columnsDesktop, 10) || 4;

      this.columnsClass = this.getColumnsClass(this.columnsDesktop);

      /*
       * Build collection map from products already
       * rendered in the initial HTML.
       */
      this.collectionMap = new Map();

      this.allItems = Array.from(
        this.slider.querySelectorAll("[data-collection]")
      );

      this.allItems.forEach((item) => {
        const collection = item.dataset.collection;

        if (!collection) return;

        if (!this.collectionMap.has(collection)) {
          this.collectionMap.set(collection, []);
        }

        this.collectionMap.get(collection).push(item);
      });

      /*
       * The first collection already exists in the HTML.
       * Mark it as loaded.
       *
       * IMPORTANT:
       * Do not modify the DOM here.
       */
      if (this.buttons.length > 0) {
        const firstCollection =
          this.buttons[0].dataset.collection;

        if (firstCollection) {
          this._fetchCache.set(firstCollection, true);
        }
      }

      /*
       * Tab clicks.
       */
      if (this.buttons.length > 0) {
        this.addEventListener("click", (event) => {
          const button = event.target.closest("button");

          if (!button || !this.contains(button)) return;

          event.preventDefault();

          const index = this.buttons.indexOf(button);

          if (index === -1) return;

          this.toggleButtons(index);
          this.toggleLinks(index);

          if (button.dataset.collection) {
            this.handleTabClick(button);
          }
        });
      }

      /*
       * Resize Flickity only when the browser
       * actually resizes.
       */
      this._onResize = debounce(() => {
        const flickity = this.getFlickity();

        if (flickity) {
          flickity.resize();
        }
      }, 150);

      window.addEventListener("resize", this._onResize);

      /*
       * Shopify Theme Editor support.
       */
      if (Shopify.designMode) {
        this._onBlockSelect = (event) => {
          const index = this.buttons.indexOf(event.target);

          if (index !== -1) {
            this.buttons[index].dispatchEvent(
              new Event("click")
            );
          }
        };

        this.addEventListener(
          "shopify:block:select",
          this._onBlockSelect
        );
      }
    }

    disconnectedCallback() {
      if (this._onResize) {
        window.removeEventListener(
          "resize",
          this._onResize
        );
      }

      if (this._onBlockSelect) {
        this.removeEventListener(
          "shopify:block:select",
          this._onBlockSelect
        );
      }
    }

    getFlickity() {
      return this.slider
        ? Flickity.data(this.slider)
        : null;
    }

    toggleButtons(index) {
      this.buttons.forEach((button) => {
        button.classList.remove("active");
      });

      if (this.buttons[index]) {
        this.buttons[index].classList.add("active");
      }
    }

    toggleLinks(index) {
      if (!this.links.length || !this.links[index]) {
        return;
      }

      this.links.forEach((link) => {
        link.classList.remove("active");
      });

      this.links[index].classList.add("active");
    }

    getColumnsClass(columns) {
      switch (columns) {
        case 2:
          return "medium-6";

        case 3:
          return "medium-4";

        case 4:
          return "medium-6 large-3";

        case 5:
          return "small-6 medium-4 large-1/5";

        default:
          return "medium-6 large-4";
      }
    }

    toggleCollection(collection) {
      if (!this.slider) return;

      const flickity = this.getFlickity();

      const items = this.collectionMap.get(collection);

      if (!items || !items.length) {
        return;
      }

      const flickitySlider =
        this.slider.querySelector(".flickity-slider");

      /*
       * Remove active slide class.
       */
      this.allItems.forEach((item) => {
        item.classList.remove("carousel__slide");
      });

      /*
       * Add active slide class.
       */
      items.forEach((item) => {
        item.classList.add("carousel__slide");
      });

      /*
       * Only rearrange the DOM when changing
       * to another collection.
       */
      if (flickity && flickitySlider) {
        for (let i = items.length - 1; i >= 0; i--) {
          const item = items[i];

          if (flickitySlider.firstChild !== item) {
            flickitySlider.insertBefore(
              item,
              flickitySlider.firstChild
            );
          }
        }

        /*
         * Recalculate Flickity without
         * requestAnimationFrame.
         */
        flickity.reloadCells();
        flickity.resize();
      }
    }

    handleTabClick(button) {
      const collection =
        button.dataset.collection;

      if (!collection) return;

      /*
       * Already loaded.
       */
      if (this._fetchCache.has(collection)) {
        this.toggleCollection(collection);
        return;
      }

      const collectionUrl =
        button.dataset.collectionUrl;

      if (!collectionUrl) return;

      /*
       * Load only when clicked.
       */
      this.fetchCollection(
        collection,
        collectionUrl
      ).then(() => {
        this.toggleCollection(collection);
      });
    }

    fetchCollection(collection, url) {
      /*
       * Already loaded.
       */
      if (this._fetchCache.has(collection)) {
        return Promise.resolve();
      }

      /*
       * Already being loaded.
       */
      if (this._fetchPromises.has(collection)) {
        return this._fetchPromises.get(collection);
      }

      const promise = fetch(
        url + "?section_id=collection-tabs-ajax"
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              "Collection request failed"
            );
          }

          return response.text();
        })

        .then((html) => {
          const documentHTML =
            new DOMParser().parseFromString(
              html,
              "text/html"
            );

          /*
           * Get only the required products.
           */
          const items = Array.from(
            documentHTML.querySelectorAll(
              "[data-collection]"
            )
          ).slice(
            0,
            this.productLimit
          );

          const flickitySlider =
            this.slider.querySelector(
              ".flickity-slider"
            ) || this.slider;

          /*
           * Add products to slider.
           */
          items.forEach((item) => {
            this.columnsClass
              .split(" ")
              .forEach((className) => {
                item.classList.add(className);
              });

            flickitySlider.appendChild(item);
          });

          /*
           * Register collection.
           */
          if (!this.collectionMap.has(collection)) {
            this.collectionMap.set(
              collection,
              []
            );
          }

          items.forEach((item) => {
            this.collectionMap
              .get(collection)
              .push(item);
          });

          /*
           * Update product list.
           */
          this.allItems =
            Array.from(
              this.slider.querySelectorAll(
                "[data-collection]"
              )
            );

          /*
           * Mark as loaded.
           */
          this._fetchCache.set(
            collection,
            true
          );

          /*
           * Load images for the selected
           * collection.
           */
          items.forEach((item) => {
            item
              .querySelectorAll(".lazyload")
              .forEach((image) => {
                if (
                  window.lazySizes &&
                  window.lazySizes.loader
                ) {
                  window.lazySizes.loader.unveil(
                    image
                  );
                }
              });
          });

          this._fetchPromises.delete(
            collection
          );
        })

        .catch((error) => {
          console.warn(
            "Collection Tabs:",
            error
          );

          this._fetchPromises.delete(
            collection
          );
        });

      this._fetchPromises.set(
        collection,
        promise
      );

      return promise;
    }
  }

  customElements.define(
    "collection-tabs",
    CollectionTabs
  );
}